import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { drawingScaleForDisplayedWidth, fitCanvasAboveForm } from './screenshot-canvas-size';

/**
 * The pen colours the toolbar offers. The value is used directly as the canvas
 * stroke and fill style, and as the toolbar button's modifier class, so the
 * strings are the CSS colour names and not free labels.
 */
export enum PenColor {
  Green = 'green',
  Yellow = 'yellow',
  Red = 'red',
}

/** Which part of a drag a canvas pointer event is. */
enum PointerPhase {
  Down,
  Move,
  Up,
  Out,
}

/**
 * The screenshot annotation editor: a toolbar, a canvas holding the captured
 * screenshot, and a form floating at the bottom of the screen.
 *
 * The page supplies the form's contents through `<ng-content>` and keeps
 * everything about sending: what it is sending, the text box, the discard
 * question, the telemetry. This component owns only the picture and the drawing
 * on it.
 *
 * It is used by the feedback page and by the coach-message page. Before it
 * existed both pages carried their own copy of the drawing code, the toolbar
 * markup and the editor styles, so every change to the drawing had to be made
 * twice (deferred finding df-6aa8b48d3286).
 *
 * **The page drives the start, not a lifecycle hook.** The page calls
 * `initAfterEnter()` from its own `ionViewDidEnter`, because the canvas is
 * sized by measuring where the form below it ended up, and this component's
 * `ngAfterViewInit` runs before the page has finished entering.
 */
@Component({
  selector: 'cs-screenshot-annotator',
  templateUrl: 'cs-screenshot-annotator.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsScreenshotAnnotatorComponent {
  protected PenColor = PenColor;

  @ViewChild('canvas', { static: false }) canvas: ElementRef;
  @ViewChild('formFooter', { static: false }) formFooter: ElementRef;
  @ViewChild('formPlaceholder', { static: false }) formPlaceholder: ElementRef;

  /**
   * The captured screenshot to draw on, as a data URL. The page reads it from
   * the app's screenshot round trip; this component never touches that trip, so
   * one owner decides when the capture is consumed.
   */
  @Input() imageDataUrl: string | undefined;

  /**
   * The toolbar's close button was pressed. What closing means differs per page
   * — one asks a discard question of its own, the other leaves through the
   * page's leave guard — so the page decides.
   */
  @Output() closeRequested = new EventEmitter<void>();

  lineColor: PenColor = PenColor.Red;
  editEnabled: boolean = true;

  private lineWidth: number = 2;

  // the drag in progress. prev and curr are image coordinates, not screen ones:
  // the drawing context is scaled so that both mean the same thing
  private isDragging: boolean = false;
  private prevX: number = 0;
  private prevY: number = 0;
  private currX: number = 0;
  private currY: number = 0;

  private pointerListenersAttached: boolean = false;

  constructor(private cdRef: ChangeDetectorRef) {
  }

  /**
   * Puts the screenshot on the canvas and makes it drawable. Safe to call more
   * than once: the pointer listeners are attached only the first time.
   */
  initAfterEnter(): void {
    this.attachPointerListenersOnce();
    this.redrawFromSource();
    this.syncFormPlaceholderHeight();
  }

  /**
   * The screenshot with the user's drawing on it, ready to send.
   *
   * Deliberately does not guard against a missing canvas. The canvas is part of this
   * component's own template, so it is there whenever this component is, and both callers
   * already check that the component is on screen. If it were somehow missing this
   * throws, which surfaces — the feedback page catches it and tells the user the send
   * failed, and on the coach page it reaches the global handler and Sentry. Answering
   * with an empty string instead would send the message without the picture and report
   * success, with nothing anywhere saying the drawing was dropped.
   */
  getAnnotatedImageDataUrl(): string {
    const canvasElement = this.canvas.nativeElement as HTMLCanvasElement;
    return canvasElement.toDataURL('image/jpeg', 1.0);
  }

  protected onCloseClicked() {
    this.closeRequested.emit();
  }

  protected onResetClicked() {
    // redrawing the untouched screenshot is what wipes the drawing: assigning
    // the canvas its size again clears everything that was on it
    this.redrawFromSource();
  }

  protected toggleEditEnabled() {
    this.editEnabled = !this.editEnabled;
    this.cdRef.markForCheck();
  }

  protected setLineColor(color: PenColor) {
    this.lineColor = color;
    this.cdRef.markForCheck();
  }

  /**
   * The form below the canvas is positioned absolutely so it can grow upwards
   * over the picture when the text box is focused. This reserves the grid space
   * it would otherwise have taken, so the canvas stops above it.
   */
  private syncFormPlaceholderHeight() {
    // if both the placeholder and the form are on screen, match their heights
    if (this.formPlaceholder?.nativeElement && this.formFooter?.nativeElement) {
      const formHeight = (this.formFooter.nativeElement as HTMLElement).offsetHeight;
      (this.formPlaceholder.nativeElement as HTMLElement).style.height = `${formHeight}px`;
    }
  }

  private attachPointerListenersOnce() {
    if (this.pointerListenersAttached) {
      return;
    }
    const canvasElement = this.canvas?.nativeElement as HTMLCanvasElement;
    if (!canvasElement) {
      return;
    }
    canvasElement.addEventListener('mousedown', e => this.onCanvasPointerEvent(PointerPhase.Down, e), false);
    canvasElement.addEventListener('touchstart', e => this.onCanvasPointerEvent(PointerPhase.Down, e), false);
    canvasElement.addEventListener('mousemove', e => this.onCanvasPointerEvent(PointerPhase.Move, e), false);
    canvasElement.addEventListener('touchmove', e => this.onCanvasPointerEvent(PointerPhase.Move, e), false);
    canvasElement.addEventListener('mouseup', e => this.onCanvasPointerEvent(PointerPhase.Up, e), false);
    canvasElement.addEventListener('touchend', e => this.onCanvasPointerEvent(PointerPhase.Up, e), false);
    canvasElement.addEventListener('mouseout', e => this.onCanvasPointerEvent(PointerPhase.Out, e), false);
    canvasElement.addEventListener('touchcancel', e => this.onCanvasPointerEvent(PointerPhase.Out, e), false);
    this.pointerListenersAttached = true;
  }

  /** Draws the screenshot, wiping anything already on the canvas. */
  private redrawFromSource() {
    if (!this.imageDataUrl) {
      return;
    }
    const canvasElement = this.canvas?.nativeElement as HTMLCanvasElement;
    if (!canvasElement) {
      return;
    }
    const ctx = canvasElement.getContext('2d');
    const img = new Image();
    img.src = this.imageDataUrl;
    img.onload = () => {
      // giving the canvas a size also clears it and resets any scaling left
      // over from a previous draw, which is what makes reset start from scratch
      canvasElement.width = img.naturalWidth;
      canvasElement.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, 0, 0, canvasElement.width, canvasElement.height);
      // scale the drawing context so a pointer position measured on the
      // shrunken picture lands on the right pixel of the full-size one
      const scale = drawingScaleForDisplayedWidth(img.naturalWidth, img.naturalHeight, canvasElement.offsetWidth);
      ctx.scale(scale.x, scale.y);
      // keep the picture clear of the form floating at the bottom
      const formFooterElement = this.formFooter.nativeElement as HTMLElement;
      const fit = fitCanvasAboveForm(
        img.naturalWidth,
        img.naturalHeight,
        canvasElement.getBoundingClientRect().top,
        formFooterElement.getBoundingClientRect().top,
      );
      canvasElement.style.maxHeight = `${fit.maxHeightPx}px`;
      canvasElement.style.maxWidth = `${fit.maxWidthPx}px`;
    };
  }

  /**
   * Turns one mouse or touch event on the canvas into a mark: a dot where the
   * pointer went down, and a line segment for each step of a drag after that.
   */
  private onCanvasPointerEvent(phase: PointerPhase, e: MouseEvent | TouchEvent) {
    if (!this.editEnabled) {
      return;
    }
    e.stopPropagation();
    e.stopImmediatePropagation();
    e.preventDefault();
    // if touching the canvas, drop focus from whatever had it — usually the text
    // box — so the form collapses out of the way. this reaches outside the
    // component on purpose: the collapse is done in CSS by :focus-within, so
    // there is nothing for the page to be told about
    if (phase === PointerPhase.Down) {
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && activeElement.blur) {
        activeElement.blur();
      }
    }
    const canvasElement = this.canvas.nativeElement as HTMLCanvasElement;
    const ctx = canvasElement.getContext('2d');
    let offsetX: number;
    let offsetY: number;
    if (e instanceof MouseEvent) {
      offsetX = e.offsetX;
      offsetY = e.offsetY;
    } else {
      const touch = e.touches[0];
      if (!touch) {
        return;
      }
      const targetElement = touch.target as HTMLElement;
      const targetBounds = targetElement.getBoundingClientRect();
      offsetX = touch.pageX - targetBounds.left;
      offsetY = touch.pageY - targetBounds.top;
    }
    if (phase === PointerPhase.Down) {
      this.prevX = this.currX;
      this.prevY = this.currY;
      this.currX = offsetX;
      this.currY = offsetY;
      this.isDragging = true;
      // a tap with no movement still leaves a mark, so put a dot down at once
      ctx.beginPath();
      ctx.fillStyle = this.lineColor;
      ctx.fillRect(this.currX, this.currY, this.lineWidth, this.lineWidth);
      ctx.closePath();
    }
    if (phase === PointerPhase.Up || phase === PointerPhase.Out) {
      this.isDragging = false;
    }
    if (phase === PointerPhase.Move) {
      if (this.isDragging) {
        this.prevX = this.currX;
        this.prevY = this.currY;
        this.currX = offsetX;
        this.currY = offsetY;
        this.drawSegment();
      }
    }
  }

  /** Draws the line from where the pointer was to where it is now. */
  private drawSegment() {
    const canvasElement = this.canvas.nativeElement as HTMLCanvasElement;
    const ctx = canvasElement.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(this.prevX, this.prevY);
    ctx.lineTo(this.currX, this.currY);
    ctx.strokeStyle = this.lineColor;
    ctx.lineWidth = this.lineWidth;
    ctx.stroke();
    ctx.closePath();
  }
}
